import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import UploadForm from "components/shared-components/UploadForm";
import {
  useDownloadImportTemplate,
  useListImportType,
  useUploadImport,
  useUploadResource,
} from "hooks/import-management/useImport";
import React, { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import * as yup from "yup";

const RUNNER_SCHEDULE_IMPORT_TYPE = "50";

const parseInputDateToLocalDate = value => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const isMonday = value => {
  const date = parseInputDateToLocalDate(value);
  return date ? date.getDay() === 1 : false;
};

const isNotFutureDate = value => {
  const date = parseInputDateToLocalDate(value);
  if (!date) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date <= today;
};

const formatDateToDDMMYYYY = value => {
  const date = parseInputDateToLocalDate(value);
  if (!date) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const isDisabledNotMonday = date => {
  return date.getDay() !== 1;
};

const Import = props => {
  const { t, i18n } = useTranslation(["importManagement"]);
  const navigate = useNavigate();
  const location = useLocation();

  const [importTypeList, setImportTypeList] = useState([]);
  const [resourceId, setResourceId] = useState(null);
  const [importTemplateName, setImportTemplateName] = useState(null);

  const onFetchListImportTypeSuccess = async data => {
    const result = [];
    for (const importTypes in data) {
      result.push({ label: data[importTypes], value: importTypes });
    }

    if (location.state?.isFocusTransport && location.state?.code) {
      const typeFind = result.find(
        item => item.value === String(location.state?.code)
      );
      if (typeFind) {
        setValue("import_type", typeFind, { shouldValidate: true });
        setImportTemplateName(typeFind.label);
      }
    }

    setImportTypeList(result);
  };

  const onUploadSuccess = response => {
    if (response?.data?.id) {
      setResourceId(response?.data?.id);
    }
  };

  const onUploadImportSuccess = response => {
    navigate({ pathname: "/import-management/list" });
  };

  const onDownloadImportTemplateSuccess = data => {
    const link = document.createElement("a");
    link.href = data.fileUrl;
    link.setAttribute("download", `Import_Template_${importTemplateName}.xlsx`);
    document.body.appendChild(link);
    link.click();
  };

  const { isFetching: isLoadingImportType, refetch: fetchListImportType } =
    useListImportType(onFetchListImportTypeSuccess);

  const { mutate: uploadResource } = useUploadResource(onUploadSuccess);

  const { mutate: uploadImport } = useUploadImport(onUploadImportSuccess);

  const { mutate: downloadImportTemplate } = useDownloadImportTemplate(
    onDownloadImportTemplateSuccess
  );

  const schema = yup.object().shape({
    resource: yup
      .object()
      .required(t("form.validate.select", { title: t("form.file") }))
      .nullable(),
    import_type: yup
      .object()
      .required(t("form.validate.select", { title: t("form.import_type") })),
    start_date: yup.string().when("import_type", {
      is: importType => importType?.value === RUNNER_SCHEDULE_IMPORT_TYPE,
      then: schema =>
        schema
          .required(t("form.validate.select", { title: t("form.start_date") }))
          .test("is-monday", t("form.validate.start_date_monday"), value =>
            isMonday(value)
          )
          .test(
            "is-not-future-date",
            t("form.validate.start_date_not_future"),
            value => isNotFutureDate(value)
          ),
      otherwise: schema => schema.notRequired(),
    }),
    loops: yup.number().when("import_type", {
      is: importType => importType?.value === RUNNER_SCHEDULE_IMPORT_TYPE,
      then: schema =>
        schema
          .transform((value, originalValue) => {
            if (originalValue === "" || originalValue === null) {
              return undefined;
            }

            return Number(originalValue);
          })
          .typeError(t("form.validate.loops_number"))
          .required(t("form.validate.select", { title: t("form.loops") }))
          .integer(t("form.validate.loops_integer"))
          .min(1, t("form.validate.loops_min")),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      resource: undefined,
      import_type: undefined,
      start_date: "",
      loops: undefined,
    },
  });

  const selectedImportType = watch("import_type");
  const isRunnerScheduleImport =
    selectedImportType?.value === RUNNER_SCHEDULE_IMPORT_TYPE;

  useEffect(() => {
    fetchListImportType();
    setControlFields(prev => ({
      display: {
        ...prev.display,
        import_type: true,
        btn_upload: true,
      },
      disabled: {
        ...prev.disabled,
      },
    }));
  }, []);

  const initControlFields = {
    display: {
      import_type: false,
      btn_upload: false,
    },
    disabled: {
      import_type: false,
      btn_upload: false,
    },
  };

  const [controlFields, setControlFields] = useState(initControlFields);

  const onRemoveFile = res => {
    setValue("resource", undefined);
    setResourceId(null);
  };

  const handleFiles = files => {
    if (files.length > 0) {
      const data = new FormData();
      data.append("file", files[0]);
      setValue("resource", {}, { shouldValidate: true });
      uploadResource(data);
    }
  };

  const submitForm = submitData => {
    const payload = {
      object_type: submitData.import_type.value,
      resource_id: resourceId,
      cleanExistRecords: 0,
    };

    if (isRunnerScheduleImport) {
      payload.startDate = formatDateToDDMMYYYY(submitData.start_date);
      payload.loops = Number(submitData.loops);
    }

    uploadImport(payload);
  };

  const onDownloadImportTemplate = () => {
    downloadImportTemplate({ fileId: getValues("import_type").value });
  };

  const handleImportTypeChange = data => {
    setValue("import_type", data, { shouldValidate: true });
    setImportTemplateName(data.label);

    if (data?.value !== RUNNER_SCHEDULE_IMPORT_TYPE) {
      setValue("start_date", "", { shouldValidate: false });
      setValue("loops", undefined, { shouldValidate: false });
      clearErrors(["start_date", "loops"]);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.import")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.import")}
            breadcrumbItems={[
              { name: t("title.list"), link: "/import-management/list" },
              { name: t("title.import") },
            ]}
          />

          {isLoadingImportType ? (
            <div className="p-5 d-flex justify-content-center">
              <Spinner className="ms-2" color="primary" />
            </div>
          ) : (
            <Row className="row-cols-1">
              <Col>
                <Card>
                  <CardBody>
                    <Row className="row-cols-1">
                      {controlFields.display.import_type && (
                        <Col>
                          <FormGroup className="mb-3">
                            <Label htmlFor="import_type">
                              {t("form.import_type")}{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              control={control}
                              name="import_type"
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  isDisabled={
                                    controlFields.disabled.import_type
                                  }
                                  placeholder={t("placeholder.select", {
                                    title: t("form.import_type"),
                                  })}
                                  options={importTypeList}
                                  onChange={handleImportTypeChange}
                                  classNamePrefix="select2-selection"
                                />
                              )}
                            />
                            {errors.import_type && (
                              <MessageFormValidate
                                error={errors.import_type.message}
                              />
                            )}
                          </FormGroup>
                        </Col>
                      )}
                    </Row>
                    {isRunnerScheduleImport && (
                      <Row className="g-3">
                        <Col className="col-12 col-md-6">
                          <FormGroup className="mb-3">
                            <Label htmlFor="start_date">
                              {t("form.start_date")}{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              control={control}
                              name="start_date"
                              render={({ field }) => (
                                <Flatpickr
                                  id="start_date"
                                  className="form-control d-block Flatpickr__Input"
                                  placeholder={t("placeholder.select", {
                                    title: t("form.start_date"),
                                  })}
                                  value={field.value || ""}
                                  options={{
                                    altInput: true,
                                    altFormat: "d/m/Y",
                                    dateFormat: "Y-m-d",
                                    maxDate: getTodayDateInputValue(),
                                    disable: [isDisabledNotMonday],
                                    locale: {
                                      firstDayOfWeek: 1,
                                    },
                                  }}
                                  onChange={(_, dateStr) =>
                                    field.onChange(dateStr)
                                  }
                                />
                              )}
                            />
                            {errors.start_date && (
                              <MessageFormValidate
                                error={errors.start_date.message}
                              />
                            )}
                          </FormGroup>
                        </Col>
                        <Col className="col-12 col-md-6">
                          <FormGroup className="mb-3">
                            <Label htmlFor="loops">
                              {t("form.loops")}{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              control={control}
                              name="loops"
                              render={({ field }) => (
                                <Input
                                  id="loops"
                                  type="number"
                                  min={1}
                                  step={1}
                                  placeholder={t("placeholder.enter", {
                                    title: t("form.loops"),
                                  })}
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={event =>
                                    field.onChange(event.target.value)
                                  }
                                />
                              )}
                            />
                            {errors.loops && (
                              <MessageFormValidate
                                error={errors.loops.message}
                              />
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <FormGroup className="mb-3">
                        <Label htmlFor="avatar">{t("form.upload")}</Label>
                        <UploadForm
                          removeFile={onRemoveFile}
                          multiple={false}
                          handleFiles={files => handleFiles(files)}
                          accept=".xlsx"
                        ></UploadForm>
                        <input
                          style={{ display: "none" }}
                          {...register("resource")}
                        ></input>
                        {errors.resource && (
                          <MessageFormValidate
                            error={errors.resource.message}
                          />
                        )}
                      </FormGroup>
                    </Row>

                    {importTemplateName && (
                      <div
                        style={{ color: "blue", cursor: "pointer" }}
                        onClick={onDownloadImportTemplate}
                      >
                        {`${t(
                          "form.download_template"
                        )} (${importTemplateName})`}
                      </div>
                    )}

                    <Row className="justify-content-end">
                      <Col className="col-auto">
                        <Link
                          to="/import-management/list"
                          className="btn btn-danger btn-label"
                        >
                          <i className="fas fa-undo label-icon"></i>
                          {t("form.cancel")}
                        </Link>
                      </Col>

                      {controlFields.display.btn_upload && (
                        <Col className="col-auto">
                          <Button
                            color="primary"
                            className="btn-label"
                            type="button"
                            onClick={() => {
                              handleSubmit(submitForm)();
                            }}
                            disabled={false}
                          >
                            <i className="far fa-save label-icon"></i>
                            {t("form.btn_upload")}
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Import;
